// What a device says about its own boot and build, in staff's words. The
// service decides what a report shows (`api/src/devices/integrity.ts`); this
// only names it. Self-reported, so a sign and not a proof.

import { IntegritySignal, type Device } from '@motionsafe/ms-console-api/admin/v1/device_pb.js';

/** A finding as a badge says it: a word or two. */
export const SIGNAL_WORD: Record<IntegritySignal, string> = {
  [IntegritySignal.UNSPECIFIED]: '',
  [IntegritySignal.BOOTLOADER_UNLOCKED]: 'unlocked',
  [IntegritySignal.VERIFIED_BOOT]: 'unverified boot',
  [IntegritySignal.ROOT_TRACES]: 'root',
  [IntegritySignal.DEBUGGABLE_BUILD]: 'debug build',
  [IntegritySignal.TEST_KEYS]: 'test keys',
  [IntegritySignal.DEVELOPER_OPTIONS]: 'developer options',
  [IntegritySignal.USB_DEBUGGING]: 'USB debugging',
  [IntegritySignal.WIRELESS_DEBUGGING]: 'wireless debugging',
};

/** The device's most serious finding, the service lists them in that order; null when it shows none. */
export function firstFinding(d: Device): { word: string; critical: boolean; more: number } | null {
  const [first, ...rest] = d.integrityFindings;
  return first ? { word: SIGNAL_WORD[first.signal], critical: first.critical, more: rest.length } : null;
}

/** Whether a finding of `signal` is in the device's report, and how serious. */
export function finding(d: Device, ...signals: IntegritySignal[]): 'critical' | 'warning' | null {
  const found = d.integrityFindings.filter((f) => signals.includes(f.signal));
  return found.some((f) => f.critical) ? 'critical' : found.length ? 'warning' : null;
}

/** `ro.boot.vbmeta.device_state`, else `ro.boot.flash.locked` in the same words; '' when neither was readable. */
export function bootloaderWord(vbmeta: string, flashLocked: string): string {
  if (vbmeta) return vbmeta;
  return flashLocked === '1' ? 'locked' : flashLocked === '0' ? 'unlocked' : '';
}
